"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EPISODIC_EXPECTATIONS = exports.ANY_ADOPTING_STATE = void 0;
const window_1 = require("./window");
/**
 * ── Episodic generation, 40 CFR 262 subpart L ──────────────────────────────
 *
 * The provision's own hand-authored expectations — reviewed ONCE, here, rather
 * than restated in every jurisdiction's table. The same discipline as the
 * jurisdiction tables applies (see the vocabulary header in ../rules/types.ts):
 * a row changes only when a regulatory specialist says the law reads
 * differently, never to make the module below pass, and rows are never
 * regenerated from the implementation.
 *
 * These rows describe the relief the regulation grants (`getEpisodicRelief`).
 * Only the state axis also describes engine behavior; the LQG row is advisory
 * — see `index.ts`.
 */
/**
 * The state axis a claim can constrain. `ADOPTING` stands for every state no
 * row names — and for a facility with no recorded state, which answers under
 * federal law. Naming it rather than omitting the axis is what lets the two
 * facts (per-state adoption, federal tier) be stated without overlapping.
 */
exports.ANY_ADOPTING_STATE = "ADOPTING";
/**
 * 262.230 is subpart L's applicability provision, and it opens the relief to a
 * VSQG or an SQG only. Federal in every state, so it is the same row wherever
 * the facility sits.
 */
const NO_RELIEF_FOR_LQG = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.230",
    jurisdiction: "FEDERAL",
};
/**
 * Cited federally on purpose. Nothing in a non-adopting state's own code
 * mentions subpart L — the provision that withholds it is the federal rule
 * saying an authorized state need not take up a less-stringent federal change.
 */
const NOT_ADOPTED = {
    kind: "NO_LIMIT",
    citation: "40 CFR 271.1(i)(2)",
    jurisdiction: "FEDERAL",
};
const NOT_ADOPTED_NOTE = "Episodic generation is relief added by the 2016 Generator Improvements Rule, and a " +
    "less-stringent federal provision does not take effect in an authorized state until that " +
    "state adopts it. A facility here may still declare an event as an EPA-notification record, " +
    "but its containers stay on the state's clocks, its waste stays under the on-site cap, and " +
    "the surge still counts toward its generator category. Unconfirmed by a regulatory " +
    "specialist; deliberately the more protective reading.";
exports.EPISODIC_EXPECTATIONS = [
    // ── States that have not adopted subpart L ─────────────────────────────
    // These rows span every generator status: nothing about the facility's tier
    // can unlock a provision the state never took up.
    {
        when: { state: "CA" },
        expect: { clock: NOT_ADOPTED },
        note: `California (DTSC) has not adopted 40 CFR 262 subpart L. ${NOT_ADOPTED_NOTE}`,
    },
    {
        when: { state: "CT" },
        expect: { clock: NOT_ADOPTED },
        note: "Connecticut (DEEP) has not adopted 40 CFR 262 subpart L: its program incorporates the " +
            `CFR as of July 1, 2000, predating the 2016 Generator Improvements Rule. ${NOT_ADOPTED_NOTE}`,
    },
    {
        when: { state: "MN" },
        expect: { clock: NOT_ADOPTED },
        note: `Minnesota (MPCA) has not adopted 40 CFR 262 subpart L. ${NOT_ADOPTED_NOTE}`,
    },
    // ── The federal tier rule, in every state that did adopt it ────────────
    {
        when: { state: exports.ANY_ADOPTING_STATE, generatorStatus: "LQG" },
        expect: { clock: NO_RELIEF_FOR_LQG },
        note: "Subpart L is relief for VSQGs and SQGs; 262.230 offers a large quantity generator " +
            "nothing, because its standing 90-day clock already governs whatever a one-off surge " +
            "adds. An LQG may still declare an event; the regulation grants it nothing extra.",
    },
    {
        when: { state: exports.ANY_ADOPTING_STATE, generatorStatus: "SQG" },
        expect: { clock: window_1.EPISODIC_WINDOW },
        note: "The surge waste must move within 60 days of the EVENT's start — 262.232(b)(4) — which " +
            "is both shorter than the SQG's standing 180/270-day clock and dated from a different " +
            "thing. The 200-mile extension is a 262.16(c) provision and does not reach episodic " +
            "waste, so this row spans that axis.",
    },
    {
        when: { state: exports.ANY_ADOPTING_STATE, generatorStatus: "VSQG" },
        expect: { clock: window_1.EPISODIC_WINDOW },
        note: "262.232(a)(4) gives a VSQG's episodic waste the one central deadline it has: 60 days " +
            "from the event's start, where 262.14 otherwise imposes no clock at all.",
    },
    {
        when: { state: exports.ANY_ADOPTING_STATE, generatorStatus: null },
        expect: { clock: window_1.EPISODIC_WINDOW },
        note: "An unresolved federal status with a declared event still gets the 60-day window: the " +
            "event's existence asserts VSQG/SQG standing (declaring one is itself a subpart L act), " +
            "and withholding it would report no deadline where the law imposes one — the less " +
            "protective failure. Unconfirmed by a regulatory specialist.",
    },
];
//# sourceMappingURL=expectations.js.map